import { getFCMToken, onForegroundMessage, initializeFirebase } from '../config/firebase';
import api from './api';

class NotificationService {
  constructor() {
    this.fcmToken = null;
    this.isInitialized = false;
    this.foregroundCallback = null;
    // Detect if running in Electron (FCM not available in Electron)
    this.isElectron = !!(window.electronAPI?.isElectron);
  }

  /**
   * Initialize the notification service
   */
  async initialize() {
    if (this.isInitialized) {
      return true;
    }

    // Skip FCM initialization in Electron - uses native notifications instead
    if (this.isElectron) {
      console.log('NotificationService: Running in Electron - using native notifications');
      this.isInitialized = true;
      return true;
    }

    try {
      await initializeFirebase();
      this.isInitialized = true;
      console.log('NotificationService initialized');
      return true;
    } catch (error) {
      console.error('Failed to initialize NotificationService:', error);
      return false;
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Check if notification permission is granted
   */
  isPermissionGranted() {
    return Notification.permission === 'granted';
  }

  /**
   * Get notification status: 'active', 'denied', 'failed', 'error', or 'pending'
   */
  getStatus() {
    return this.notificationStatus || 'pending';
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Push notifications not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Show notification (works in both browser and Electron)
   */
  showNotification(title, body, type = 'info') {
    // Use Electron native notifications if available
    if (this.isElectron && window.electronAPI?.showNotification) {
      window.electronAPI.showNotification({ title, body });
      return;
    }

    // Fallback to browser Notification API
    if (this.isPermissionGranted()) {
      new Notification(title, { body, icon: '/logo192.png' });
    }
  }

  /**
   * Get FCM token and optionally register with server
   */
  async getToken(registerWithServer = false) {
    // Skip FCM token registration in Electron - Push API not available
    // But notification service still works via showNotification()
    if (this.isElectron) {
      console.log('Electron detected - FCM token skipped, using native notifications');
      return 'electron-native';
    }

    try {
      // Request permission first
      const hasPermission = await this.requestPermission();

      if (!hasPermission) {
        console.warn('Notification permission not granted');
        this.notificationStatus = 'denied';
        return null;
      }

      // Get FCM token
      const token = await getFCMToken();

      if (token) {
        this.fcmToken = token;
        this.notificationStatus = 'active';
        console.log('FCM token obtained');

        if (registerWithServer) {
          await this.registerTokenWithServer(token);
        }
      } else {
        console.warn('FCM token was null — push notifications will not work');
        this.notificationStatus = 'failed';
      }

      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      this.notificationStatus = 'error';
      return null;
    }
  }

  /**
   * Register FCM token with backend server
   */
  async registerTokenWithServer(token = null) {
    const fcmToken = token || this.fcmToken;

    if (!fcmToken) {
      console.warn('No FCM token available to register');
      return false;
    }

    try {
      await api.post('/auth/fcm-token', {
        fcmToken: fcmToken,
        platform: 'web'
      });
      console.log('FCM token registered with server');
      return true;
    } catch (error) {
      console.error('Failed to register FCM token with server:', error);
      return false;
    }
  }

  /**
   * Unregister FCM token from server (call on logout)
   */
  async unregisterTokenFromServer() {
    try {
      await api.delete('/auth/fcm-token');
      this.fcmToken = null;
      console.log('FCM token unregistered from server');
      return true;
    } catch (error) {
      console.error('Failed to unregister FCM token from server:', error);
      return false;
    }
  }

  /**
   * Subscribe to foreground messages
   */
  onMessage(callback) {
    this.foregroundCallback = callback;

    // Skip FCM foreground listener in Electron (handled by socket events + native notifications)
    if (this.isElectron) {
      console.log('Electron: Foreground messages handled via socket + native notifications');
      return;
    }

    onForegroundMessage((payload) => {
      console.log('Foreground message received:', payload);

      const notification = {
        title: payload.notification?.title || 'Foodwinc Restaurant',
        body: payload.notification?.body || '',
        type: payload.data?.type || 'general',
        orderId: payload.data?.orderId,
        data: payload.data || {}
      };

      if (this.foregroundCallback) {
        this.foregroundCallback(notification);
      }
    });
  }

  /**
   * Get notification type for styling
   */
  getNotificationType(type) {
    switch (type) {
      case 'new_order':
        return 'success';
      case 'order_cancelled':
        return 'error';
      case 'order_update':
        return 'info';
      default:
        return 'info';
    }
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type) {
    switch (type) {
      case 'new_order':
        return '🔔';
      case 'order_cancelled':
        return '❌';
      case 'order_update':
        return '📦';
      case 'delivery_assigned':
        return '🚴';
      case 'order_delivered':
        return '✅';
      default:
        return '📢';
    }
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
